import { all, takeLatest, call, put, delay } from 'redux-saga/effects';
import Axios, { AxiosResponse } from 'axios';
import {
  getOrganisationsSuccess,
  getOrganisationsFailure,
  updateMultiOrganisationsSuccess,
  updateMultiOrganisationsFailure,
  updateMultiOrganisationsReset,
  createOrganisationSuccess,
  createOrganisationFailure,
  createOrganisationReset,
  updateOrganisationFailure,
  updateOrganisationSuccess,
  updateOrganisationReset,
  getOrganisationGroupsSuccess,
  getOrganisationGroupsFailure,
  getOrganisationDetailSuccess,
  getOrganisationDetailFailure,
  getOrganisationUpdateLogsSuccess,
  getOrganisationUpdateLogsFailure,
  getOrganisationTransactionLimitsSuccess,
  getOrganisationTransactionLimitsFailure,
  addOrganisationTransactionLimitsSuccess,
  addOrganisationTransactionLimitsFailure,
  addOrganisationTransactionLimitsReset,
  removeOrganisationTransactionLimitsSuccess,
  removeOrganisationTransactionLimitsFailure,
  removeOrganisationTransactionLimitsReset,
  suspendOrganisationSuccess,
  suspendOrganisationFailure
} from './actions';
import { SysAdminOrgActionTypes } from './types';
import {
  getOrganisations,
  getOrganisationTransactionLimits,
  addOrganisationTransactionLimits,
  removeOrganisationTransactionLimits,
  updateMultiOrganisations,
  createNewOrganisation,
  updateOrganisation,
  getOrganisationGroups,
  getOrganisationDetails,
  getOrganisationUpdateLogs,
  suspendOrganisation
} from 'services/super-admin.service';

function* handleGetOrganisations(request: any) {
  const emptyErrorText = 'Unable to get organisations.';
  try {
    const response: AxiosResponse = yield call(
      getOrganisations,
      request.payload
    );
    if (Array.isArray(response?.data?.organisations)) {
      yield put(
        getOrganisationsSuccess({
          count: response.data.count,
          data: response.data.organisations
        })
      );
    } else {
      yield put(getOrganisationsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(getOrganisationsFailure(msg || emptyErrorText));
  }
}

function* handleGetOrganisationDetails(request: any) {
  const emptyErrorText = 'Unable to get organisation.';
  try {
    const response: AxiosResponse = yield call(
      getOrganisationDetails,
      request.payload
    );
    if (response?.data?.id) {
      yield put(getOrganisationDetailSuccess(response.data));
    } else {
      yield put(getOrganisationDetailFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(getOrganisationsFailure(msg || emptyErrorText));
  }
}

function* handleGetOrganisationGroups() {
  const emptyErrorText = 'Unable to get organisation groups.';
  try {
    const response: AxiosResponse = yield call(getOrganisationGroups);
    if (Array.isArray(response?.data)) {
      yield put(getOrganisationGroupsSuccess(response.data));
    } else {
      yield put(getOrganisationGroupsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(getOrganisationGroupsFailure(msg || emptyErrorText));
  }
}

function* handleUpdateMultiOrganisations(request: any) {
  const emptyErrorText = 'Unable to update organisations.';
  try {
    const response: AxiosResponse = yield call(
      updateMultiOrganisations,
      request.payload
    );
    if (response?.data?.success) {
      yield put(updateMultiOrganisationsSuccess());
      yield delay(2000);
      yield put(updateMultiOrganisationsReset());
    } else {
      yield put(updateMultiOrganisationsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(updateMultiOrganisationsFailure(msg || emptyErrorText));
  }
}

function* handleCreateOrganisation(request: any) {
  const emptyErrorText = 'Unable to create new organisation.';
  try {
    const response: AxiosResponse = yield call(
      createNewOrganisation,
      request.payload
    );
    if (response?.data?.status === 'success') {
      yield put(createOrganisationSuccess());
    } else {
      yield put(createOrganisationFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(createOrganisationFailure(msg || emptyErrorText));
  }
  yield delay(2000);
  yield put(createOrganisationReset());
}

function* handleUpdateOrganisation(request: any) {
  const emptyErrorText = 'Unable to update organisation.';
  try {
    const response: AxiosResponse = yield call(
      updateOrganisation,
      request.payload.id,
      request.payload.data
    );
    if (response?.data?.id) {
      yield put(updateOrganisationSuccess(response.data));
      yield delay(2000);
      yield put(updateOrganisationReset());
    } else {
      yield put(updateOrganisationFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(updateOrganisationFailure(msg || emptyErrorText));
  }
  yield delay(1000);
  yield put(updateOrganisationReset());
}

function* handleSuspendOrganisation(request: any) {
  const emptyErrorText = 'Unable to suspend organisation.';
  try {
    const response: AxiosResponse = yield call(
      suspendOrganisation,
      request.payload.id,
      request.payload.data
    );
    if (response?.data) {
      yield put(suspendOrganisationSuccess(response.data));
    } else {
      yield put(suspendOrganisationFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(suspendOrganisationFailure(msg || emptyErrorText));
  }
}

function* handleGetOrganisationUpdateLogs(request: any) {
  const emptyErrorText = 'Unable to get organisation update logs.';
  try {
    const response: AxiosResponse = yield call(
      getOrganisationUpdateLogs,
      request.payload.organisationId,
      request.payload.params
    );
    if (Array.isArray(response?.data?.organisationAuditLogs)) {
      yield put(
        getOrganisationUpdateLogsSuccess({
          count: response.data.count,
          data: response.data.organisationAuditLogs
        })
      );
    } else {
      yield put(getOrganisationUpdateLogsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(getOrganisationUpdateLogsFailure(msg || emptyErrorText));
  }
}

function* handleGetOrganisationTransactionLimits(request: any) {
  const emptyErrorText = 'Unable to get organisation transaction limits.';
  try {
    const response: AxiosResponse = yield call(
      getOrganisationTransactionLimits,
      request.payload.organisationId,
      request.payload.params
    );
    if (Array.isArray(response?.data?.data)) {
      yield put(
        getOrganisationTransactionLimitsSuccess({
          count: response.data.count,
          data: response.data.data
        })
      );
    } else {
      yield put(getOrganisationTransactionLimitsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(getOrganisationTransactionLimitsFailure(msg || emptyErrorText));
  }
}

function* handleAddOrganisationTransactionLimits(request: any) {
  const emptyErrorText = 'Unable to add transaction limits.';
  try {
    const response: AxiosResponse = yield call(
      addOrganisationTransactionLimits,
      request.payload.organisationId,
      request.payload.data
    );
    if (response?.data?.id) {
      yield put(addOrganisationTransactionLimitsSuccess());
    } else {
      yield put(addOrganisationTransactionLimitsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(addOrganisationTransactionLimitsFailure(msg || emptyErrorText));
  }
  yield delay(2000);
  yield put(addOrganisationTransactionLimitsReset());
}

function* handleRemoveOrganisationTransactionLimits(request: any) {
  const emptyErrorText = 'Unable to remove transaction limits.';
  try {
    const response: AxiosResponse = yield call(
      removeOrganisationTransactionLimits,
      request.payload.organisationId,
      request.payload.restrictionLimitId
    );
    if (response?.data?.success) {
      yield put(removeOrganisationTransactionLimitsSuccess());
    } else {
      yield put(removeOrganisationTransactionLimitsFailure(emptyErrorText));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      removeOrganisationTransactionLimitsFailure(msg || emptyErrorText)
    );
  }
  yield delay(2000);
  yield put(removeOrganisationTransactionLimitsReset());
}

function* watchGetOrganisations() {
  yield takeLatest(
    SysAdminOrgActionTypes.GET_ORGANISATIONS_START,
    handleGetOrganisations
  );
}

function* watchGetOrganisationDetails() {
  yield takeLatest(
    SysAdminOrgActionTypes.GET_ORGANISATION_DETAIL_START,
    handleGetOrganisationDetails
  );
}

function* watchGetOrganisationGroups() {
  yield takeLatest(
    SysAdminOrgActionTypes.GET_ORGANISATION_GROUPS_START,
    handleGetOrganisationGroups
  );
}

function* watchUpdateMultiOrganisations() {
  yield takeLatest(
    SysAdminOrgActionTypes.UPDATE_MULTI_ORGANISATIONS_START,
    handleUpdateMultiOrganisations
  );
}

function* watchCreateOrganisation() {
  yield takeLatest(
    SysAdminOrgActionTypes.CREATE_ORGANISATION_START,
    handleCreateOrganisation
  );
}

function* watchUpdateOrganisation() {
  yield takeLatest(
    SysAdminOrgActionTypes.UPDATE_ORGANISATION_START,
    handleUpdateOrganisation
  );
}

function* watchSuspendOrganisation() {
  yield takeLatest(
    SysAdminOrgActionTypes.SUSPEND_ORGANISATION_START,
    handleSuspendOrganisation
  );
}

function* watchGetOrganisationUpdateLogs() {
  yield takeLatest(
    SysAdminOrgActionTypes.GET_ORGANISATION_UPDATE_LOGS_START,
    handleGetOrganisationUpdateLogs
  );
}

function* watchGetOrganisationTransactionLimits() {
  yield takeLatest(
    SysAdminOrgActionTypes.GET_ORGANISATION_TRANSACTION_LIMITS_START,
    handleGetOrganisationTransactionLimits
  );
}

function* watchAddOrganisationTransactionLimits() {
  yield takeLatest(
    SysAdminOrgActionTypes.ADD_ORGANISATION_TRANSACTION_LIMITS_START,
    handleAddOrganisationTransactionLimits
  );
}

function* watchRemoveOrganisationTransactionLimits() {
  yield takeLatest(
    SysAdminOrgActionTypes.REMOVE_ORGANISATION_TRANSACTION_LIMITS_START,
    handleRemoveOrganisationTransactionLimits
  );
}

function* sagas() {
  yield all([
    call(watchGetOrganisations),
    call(watchGetOrganisationDetails),
    call(watchGetOrganisationGroups),
    call(watchUpdateMultiOrganisations),
    call(watchCreateOrganisation),
    call(watchUpdateOrganisation),
    call(watchSuspendOrganisation),
    call(watchGetOrganisationTransactionLimits),
    call(watchAddOrganisationTransactionLimits),
    call(watchRemoveOrganisationTransactionLimits),
    call(watchGetOrganisationUpdateLogs)
  ]);
}

export default sagas;
